import { createStore } from 'vuex'
import { createToast } from "mosha-vue-toastify";
import "mosha-vue-toastify/dist/style.css";
import axios from "axios";

let hasTriggeredLogoutRedirect = false;

function getBaseHashUrl(forceReload = false) {
	const pathname = window.location.pathname || '/';
	const basePath = pathname.endsWith('/')
		? pathname
		: pathname.substring(0, pathname.lastIndexOf('/') + 1);
	if (forceReload) {
		return basePath + '?logoutRedirect=' + Date.now() + '#/';
	}
	return basePath + '#/';
}

function redirectToLanding(forceReload = true) {
	if (hasTriggeredLogoutRedirect) return;
	hasTriggeredLogoutRedirect = true;
	window.location.assign(getBaseHashUrl(forceReload));
}

function isSessionExpiredError(error) {
	const status = error?.response?.status;
	const code = error?.response?.data?.code;
	const message = error?.response?.data?.message;
	return (
		status === 401 ||
		status === 419 ||
		status === 440 ||
		code === 440 ||
		message === "Unauthenticated." ||
		message === "Session expired due to inactivity."
	);
}

function normalizeError(error, fallbackMessage = "Request failed.") {
	if (error?.response?.data) return error;
	return {
		...error,
		response: {
			status: error?.response?.status || null,
			data: {
				errors: {},
				message: error?.message || fallbackMessage
			}
		}
	};
}

export default createStore({
	state: {
		// apiUrl: "http://52.66.40.36/muddemal/",
		// download_url: "http://52.66.40.36/muddemal/",
		 apiUrl: "https://muddemal.hassan.patterneffects.in/",
		download_url: "https://muddemal.hassan.patterneffects.in/",
		//apiUrl: "http://localhost:8000/",
		//download_url: "http://localhost:8000/",
		user: null,
		token: "",
		isAdmin: false, // Separate admin flag - validated server-side, not from user object
		isLoggingOut: false,
		current_page: '',
		search_keyword: '',
		police_station_ids: [],
		years: [],
		page: ''
	},
	getters: {
		user(state) {
			return state.user;
		},
		token(state) {
			return state.token;
		},
		isAdmin(state) {
			return state.isAdmin;
		},
		isLoggingOut(state) {
			return state.isLoggingOut;
		},
		page(state) {
			return state.page;
		},
		current_page(state) {
			return state.current_page;
		},
		search_keyword(state) {
			return state.search_keyword;
		},
		police_station_ids(state) {
			return state.police_station_ids;
		},
		years(state) {
			return state.years;
		},
		downloadUrl(state) {
			return state.download_url;
		},
	},
	mutations: {
		setUser(state, user) {
			state.user = user;
		},
		setToken(state, token) {
			state.token = token;
		},
		setIsAdmin(state, isAdmin) {
			state.isAdmin = isAdmin;
		},
		setIsLoggingOut(state, isLoggingOut) {
			state.isLoggingOut = isLoggingOut;
		},
		setPage(state, page) {
			state.page = page;
		},
		setCurrentPage(state, current_page) {
			state.current_page = current_page;
		},
		setSearchKeyword(state, search_keyword) {
			state.search_keyword = search_keyword;
		},
		setPoliceStationIds(state, police_station_ids) {
			state.police_station_ids = police_station_ids;
		},
		setYears(state, years) {
			state.years = years;
		},
	},
	actions: {
		async setUser(context, payload) {
			sessionStorage.setItem("user", JSON.stringify(payload))
			await context.commit('setUser', payload);
		},
		async setToken(context, payload) {
			sessionStorage.setItem("token", payload)
			await context.commit('setToken', payload);
			// Reset logout guard once a valid session token is set again.
			if (payload) {
				await context.commit('setIsLoggingOut', false);
			}
		},
		async setPage(context, payload) {
			await context.commit('setPage', payload);
		},
		async setCurrentPage(context, payload) {
			await context.commit('setCurrentPage', payload);
		},
		async setSearchKeyword(context, payload) {
			await context.commit('setSearchKeyword', payload);
		},
		async setPoliceStationIds(context, payload) {
			await context.commit('setPoliceStationIds', payload);
		},
		async setYears(context, payload) {
			await context.commit('setYears', payload);
		},
		async logout(context) {
			await context.commit('setIsLoggingOut', true);
			sessionStorage.removeItem('user');
			sessionStorage.removeItem('token');
			await context.commit('setUser', null);
			await context.commit('setToken', "");
			await context.commit('setIsAdmin', false);
		},

		/**
		 * Check admin status from server.
		 * 
		 * This validates admin status with the server to prevent UI exposure.
		 * Even if an attacker manipulates the response, server-side validation on every API call prevents actual access.
		 */
		async checkAdmin(context) {
			return new Promise((resolve, reject) => {
				if (!context.getters.token) {
					context.commit('setIsAdmin', false);
					resolve({ data: { is_admin: false } });
					return;
				}

				axios.post(this.state.apiUrl + 'api/checkAdmin', {}, {
					headers: {
						'Authorization': 'Bearer' + ' ' + context.getters.token
					}
				}).then(function (response) {
					// Validate response structure and set admin flag
					const isAdmin = response.data && response.data.is_admin === true;
					context.commit('setIsAdmin', isAdmin);
					resolve(response);
				})
					.catch(function (error) {
						// On error, assume not admin (fail secure)
						context.commit('setIsAdmin', false);
						const normalizedError = normalizeError(error, "Unable to verify admin access.");
						if (!context.getters.isLoggingOut && isSessionExpiredError(normalizedError)) {
							context.dispatch('logout');
							redirectToLanding(true);
						}
						// Reject to allow caller to handle the error
						reject(normalizedError);
					});
			});
		},

		/**
		 * Validate admin status with server before allowing access.
		 * This is used for route guards and UI visibility checks.
		 * Returns a promise that resolves to true/false based on server validation.
		 */
		async validateAdminAccess(context) {
			try {
				const response = await context.dispatch('checkAdmin');
				return response.data && response.data.is_admin === true;
			} catch (error) {
				// On any error, deny access (fail secure)
				context.commit('setIsAdmin', false);
				return false;
			}
		},

		auth(context, payload) {
			return new Promise((resolve, reject) => {
				axios.post(this.state.apiUrl + 'api/' + payload.uri, payload.data)
					.then(function (response) {
						resolve(response);
					})
					.catch(function (error) {
						reject(error);
					});
			});
		},

		post(context, payload) {
			return new Promise((resolve, reject) => {
				if (context.getters.isLoggingOut) {
					redirectToLanding(true);
					reject(normalizeError(new Error('Skipped API call during logout.'), 'Skipped API call during logout.'));
					return;
				}
				axios.post(this.state.apiUrl + 'api/' + payload.uri, payload.data, {
					headers: {
						'Authorization': 'Bearer' + ' ' + context.getters.token
					}
				}).then(function (response) {
					resolve(response);
				})
					.catch(function (error) {
						const normalizedError = normalizeError(error);
						reject(normalizedError);
						if (!context.getters.isLoggingOut && isSessionExpiredError(normalizedError)) {
							context.dispatch('logout');
							redirectToLanding(true);
						}
					});
			});
		},

		fileDownload(context, payload) {
			return new Promise((resolve, reject) => {
				if (context.getters.isLoggingOut) {
					redirectToLanding(true);
					reject(normalizeError(new Error('Skipped download call during logout.'), 'Skipped download call during logout.'));
					return;
				}
				axios.post(this.state.apiUrl + 'api/' + payload.uri, payload.data, {
					responseType: 'blob',
					headers: {
						'Authorization': 'Bearer' + ' ' + context.getters.token
					}
				}).then(function (response) {
					resolve(response);
				})
					.catch(function (error) {
						const normalizedError = normalizeError(error);
						reject(normalizedError);
						if (!context.getters.isLoggingOut && isSessionExpiredError(normalizedError)) {
							context.dispatch('logout');
							redirectToLanding(true);
						}
					});
			});
		},

		async error(context, description) {
			if (context.getters.isLoggingOut) return;
			await createToast(
				{
					title: "Error",
					description: description || "The given data was invalid.",
				},
				{
					showIcon: true,
					hideProgressBar: true,
					type: "danger",
					position: "top-right",
				}
			);
		},

		async success(context, description) {
			await createToast(
				{
					title: "Success",
					description: description || "Data is successfully subbmitted.",
				},
				{
					showIcon: true,
					hideProgressBar: true,
					type: "success",
					position: "top-right",
				}
			);
		},

		async info(context, description) {
			await createToast(
				{
					title: "Info",
					description: description || "Not enough input data",
				},
				{
					showIcon: true,
					hideProgressBar: true,
					type: "info",
					position: "top-right",
				}
			);
		},
	}
})
